import React, { useCallback, useEffect, useMemo, useState } from "react"
import { getAllMeetingRooms, getAreaMap, getLocations, getSeatsByAreaId, getTenants, switchTenant, updateSeatsByTenantIdLocationIdAndAreaId } from "../../services/ApiServices"
import { localStorageNames, meetingRoomBaseUrl, seatBaseUrl } from "../../const"
import { useSelector } from "react-redux"
import { toast } from "react-hot-toast"
import Loader from "../Loader/Loader"
import axios from "axios"
import "./Body.scss"

import IconChair from "../../assets/svgs/IconChair"
import IconFloorPlan from "../../assets/svgs/IconFloorPlan"
import IconMapPin from "../../assets/svgs/IconMapPin"
import IconMeetingRoom from "../../assets/svgs/IconMeetingRoom"
import moment from "moment"

const defaultPrinterName = "DYMO LabelWriter 550 Turbo"
const tabs = {
    Seats: "Seats",
    Area: "Area",
    MeetingRooms: "MeetingRooms",
}

const tabList = [
    { Id: tabs?.Seats, Title: "Seat List", Icon: () => <i className="fa-solid fa-list" /> },
    { Id: tabs?.Area, Title: "Area Map", Icon: () => <IconFloorPlan height={22} width={22} /> },
    { Id: tabs?.MeetingRooms, Title: "Meeting Room List", Icon: () => <IconMeetingRoom height={25} width={25} /> },
]

const dymo = window.dymo

export default function Body() {
    const currentUser = useSelector((state) => state.APIResponse.CurrentUser)

    const isAuthorized = useMemo(() => (
        currentUser?.IsAppAdmin || currentUser?.Roles?.includes("Administrator")
    ), [currentUser?.IsAppAdmin, currentUser?.Roles])

    const [isLoading, setIsLoading] = useState(false)
    const [isAllTenantLoading, setIsAllTenantLoading] = useState(false)
    const [isMapLoading, setIsMapLoading] = useState(false)
    const [isAllSeatsLoading, setIsAllSeatsLoading] = useState(false)

    const [XMLTemplate, setXMLTemplate] = useState("")

    const localStoragePrinterName = useMemo(() => window.localStorage.getItem(localStorageNames?.printerName), [])

    const [printerName, setPrinterName] = useState(localStoragePrinterName ?? defaultPrinterName)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const [inputTenant, setInputTenant] = useState(0)
    const [inputLocation, setInputLocation] = useState(0)
    const [inputArea, setInputArea] = useState(0)

    const [allTenants, setAllTenants] = useState([])
    const [allLocations, setAllLocations] = useState([])
    const [areaMapImage, setAreaMapImage] = useState({ Image: "", naturalHeight: 0, naturalWidth: 0 })
    const [allSeats, setAllSeats] = useState([])
    const [tempAllSeats, setTempAllSeats] = useState([])
    const [allSelectedSeats, setAllSelectedSeats] = useState([])
    const [meetingRoomsData, setMeetingRoomsData] = useState({ Id: 0, MeetingRooms: [], IsLoading: false })
    const [selectedMeetingRooms, setSelectedMeetingRooms] = useState([])

    const [activeTab, setActiveTab] = useState(tabs?.Seats) // Seat | Area | MeetingRoom
    const isAllSelected = useMemo(() => {
        if (activeTab === tabs?.Seats || activeTab === tabs?.Area) {
            return allSeats?.every((seat) => allSelectedSeats?.some((selectedSeat) => selectedSeat?.Id === seat?.Id))
        } else if (activeTab === tabs?.MeetingRooms) {
            return meetingRoomsData?.MeetingRooms?.every((meetingRoom) => selectedMeetingRooms?.some((selectedMeetingRoom) => selectedMeetingRoom?.Id === meetingRoom?.Id))
        }

        return false
    }, [activeTab, allSeats, allSelectedSeats, meetingRoomsData?.MeetingRooms, selectedMeetingRooms])

    const allAreas = useMemo(() => (
        allLocations?.find((d) => d?.Id === inputLocation)?.Areas ?? []
    ), [allLocations, inputLocation])

    const editedNameList = useMemo(() => (
        allSeats?.filter((d, i) => tempAllSeats[i]?.Name?.trim() !== d?.Name?.trim())
    ), [allSeats, tempAllSeats])

    // Toggle All Items
    const toggleAllItems = useCallback(() => {
        if (activeTab === tabs?.Seats || activeTab === tabs?.Area) {
            if (isAllSelected) {
                setAllSelectedSeats((prev) => prev?.length > 0 ? [] : prev)
            } else {
                setAllSelectedSeats(allSeats)
            }
        } else if (activeTab === tabs?.MeetingRooms) {
            if (isAllSelected) {
                setSelectedMeetingRooms((prev) => prev?.length > 0 ? [] : prev)
            } else {
                setSelectedMeetingRooms(meetingRoomsData?.MeetingRooms)
            }
        }
    }, [activeTab, allSeats, isAllSelected, meetingRoomsData?.MeetingRooms])

    // Toggle checkbox
    const handleCheckboxClick = useCallback((item) => {
        if (activeTab === tabs?.Seats || activeTab === tabs?.Area) {
            setAllSelectedSeats((prevSelectedItems) => {
                if (prevSelectedItems?.includes(item)) {
                    return prevSelectedItems?.filter(i => i !== item)
                } else {
                    return [...prevSelectedItems, item]
                }
            })
        } else if (activeTab === tabs?.MeetingRooms) {
            setSelectedMeetingRooms((prevSelectedItems) => {
                if (prevSelectedItems?.includes(item)) {
                    return prevSelectedItems?.filter(i => i !== item)
                } else {
                    return [...prevSelectedItems, item]
                }
            })
        }
    }, [activeTab])

    // Change Name
    const handleChangeName = useCallback((index, value, key) => {
        let tempAllSeats = [...allSeats]
        tempAllSeats[index] = { ...tempAllSeats[index], [`${key}`]: value }
        setAllSeats(tempAllSeats)
    }, [allSeats])

    const getMapStyle = useCallback((xCoord, yCoord, width, height, isRotated, offset) => {
        const style = isRotated ? ({
            top: `calc(${(xCoord * 100) / height}% - ${offset}px - 15px)`,
            right: `calc(${(yCoord * 100) / width}% - ${offset}px + 20px)`,
        }) : ({
            left: `calc(${(xCoord * 100) / width}% - ${offset}px - 15px)`,
            top: `calc(${(yCoord * 100) / height}% - ${offset}px + 20px)`,
        })

        return style
    }, [])

    const replaceString = useCallback((Id, Name) => {
        let newString = XMLTemplate ?? ""

        if (newString) {
            newString = newString?.replaceAll("{{LabelName}}", `${Name}`)
            if (activeTab === tabs?.Seats || activeTab === tabs?.Area) {
                newString = newString?.replaceAll("{{DataString}}", `${seatBaseUrl}/${Id}`)
            } else if (activeTab === tabs?.MeetingRooms) {
                newString = newString?.replaceAll("{{DataString}}", `${meetingRoomBaseUrl}${Id}`)
            }
        }

        return newString
    }, [activeTab, XMLTemplate])

    const PrintAllLabels = useCallback(async (printerName, allSelectedItems = []) => {
        if (isAuthorized) {
            if (XMLTemplate && printerName) {
                // Check If Printer Is Connected
                setIsLoading(true)
                setIsModalOpen(false)
                window.localStorage.setItem(localStorageNames?.printerName, printerName)
                try {
                    const connectedPrinters = await dymo?.label?.framework?.getPrinters()
                    if (connectedPrinters?.byIndex?.length >= 0) {
                        if (connectedPrinters?.byIndex?.length > 0) {
                            // Print All Seats
                            for (let i = 0; i < allSelectedItems?.length; i++) {
                                await dymo?.label?.framework?.printLabel(printerName, "", replaceString(allSelectedItems[i]?.Id, allSelectedItems[i]?.Name), "")
                            }
                        }
                    } else {
                        console.error("PrintAllLabels Error:", connectedPrinters)
                        toast.error("Printer Not Connected!")
                    }
                    setIsLoading(false)
                } catch (error) {
                    console.error("GetPrinters Error:", error)
                    toast.error("Get Printers error!")
                    setIsLoading(false)
                }
            } else {
                if (!XMLTemplate) {
                    toast.error("Template not found!")
                } else if (!printerName) {
                    toast.error("Please provide printer name!")
                }
            }
        }
    }, [isAuthorized, XMLTemplate, replaceString])

    // Get Seats
    const GetSeatsByAreaId = useCallback(async (hideLoader = false) => {
        let subscribed = true

        if (isAuthorized) {
            if (inputTenant && inputLocation && inputArea) {
                !hideLoader && setIsAllSeatsLoading(true)
                getSeatsByAreaId(inputArea).then((response) => {
                    if (subscribed) {
                        setAllSelectedSeats(response?.Seats.length >= 0 ? response?.Seats : [])
                        setTempAllSeats(response?.Seats.length >= 0 ? response?.Seats : [])
                        setAllSeats(response?.Seats.length >= 0 ? response?.Seats : [])
                    }
                }).catch((error) => {
                    if (subscribed) {
                        setAllSelectedSeats((prev) => prev?.length > 0 ? [] : prev)
                        setTempAllSeats((prev) => prev?.length > 0 ? [] : prev)
                        setAllSeats((prev) => prev?.length > 0 ? [] : prev)
                        console.error("GetAllSeats Error:", error)
                    }
                }).finally(() => {
                    if (subscribed) {
                        setIsAllSeatsLoading(false)
                    }
                })
            } else {
                if (subscribed) {
                    setAllSelectedSeats((prev) => prev?.length > 0 ? [] : prev)
                    setTempAllSeats((prev) => prev?.length > 0 ? [] : prev)
                    setAllSeats((prev) => prev?.length > 0 ? [] : prev)
                }
            }
        }

        return () => {
            subscribed = false
        }
    }, [isAuthorized, inputTenant, inputLocation, inputArea])

    const UpdateSeatsByTenantIdLocationIdAndAreaId = useCallback(async () => {
        if (isAuthorized) {
            if (inputTenant && inputLocation && inputArea && editedNameList?.length > 0) {
                setIsLoading(true)
                await updateSeatsByTenantIdLocationIdAndAreaId(inputTenant, inputLocation, inputArea, editedNameList?.map((d) => ({ Id: d?.Id, Name: d?.Name }))).then(async () => {
                    await GetSeatsByAreaId(true)
                    toast.success("Record Updated Successfully.")
                }).catch((error) => {
                    toast.error(error)
                }).finally(() => {
                    setIsLoading(false)
                })
            }
        }
    }, [isAuthorized, inputTenant, inputLocation, inputArea, editedNameList, GetSeatsByAreaId])

    useEffect(() => {
        GetSeatsByAreaId()
    }, [GetSeatsByAreaId])

    // Get All MeetingRooms
    useEffect(() => {
        let subscribed = true

        if (isAuthorized && activeTab === tabs?.MeetingRooms && meetingRoomsData?.Id !== inputLocation) {
            setAreaMapImage({ Image: "", naturalHeight: 0, naturalWidth: 0 })
            if (inputLocation) {
                const requestPayload = {
                    locationId: inputLocation,
                    date: moment().format("YYYY-MM-DD"),
                }

                setMeetingRoomsData((prev) => ({ ...prev, IsLoading: true }))
                getAllMeetingRooms(requestPayload).then((response) => {
                    if (subscribed) {
                        if (response && response?.length >= 0) {
                            const MeetingRooms = response?.sort((a, b) => {
                                if (a.Name < b.Name) return -1;  // If a's Name comes before b's Name, return -1
                                if (a.Name > b.Name) return 1;   // If a's Name comes after b's Name, return 1
                                return 0;  // If they are the same, return 0
                            })
                            setSelectedMeetingRooms(MeetingRooms)
                            setMeetingRoomsData((prev) => ({ ...prev, Id: inputLocation, MeetingRooms: MeetingRooms }))
                        } else {
                            setSelectedMeetingRooms([])
                            setMeetingRoomsData((prev) => ({ ...prev, Id: inputLocation, MeetingRooms: [] }))
                            console.error("GetAreaMap Error:", response)
                        }
                    }
                }).catch((error) => {
                    if (subscribed) {
                        setSelectedMeetingRooms([])
                        setMeetingRoomsData((prev) => ({ ...prev, Id: inputLocation, MeetingRooms: [] }))
                        console.error("GetAreaMap Error:", error)
                    }
                }).finally(() => {
                    if (subscribed) {
                        setMeetingRoomsData((prev) => ({ ...prev, IsLoading: false }))
                    }
                })
            } else {
                if (subscribed) {
                    setSelectedMeetingRooms([])
                    setMeetingRoomsData((prev) => ({ ...prev, Id: inputLocation, MeetingRooms: [], IsLoading: false }))
                }
            }
        }

        return () => {
            subscribed = false
        }
    }, [isAuthorized, inputLocation, activeTab, meetingRoomsData?.Id])

    // Get Area Map
    useEffect(() => {
        let subscribed = true

        if (isAuthorized) {
            setAreaMapImage({ Image: "", naturalHeight: 0, naturalWidth: 0 })
            if (inputArea) {
                setIsMapLoading(true)
                getAreaMap(inputArea).then((response) => {
                    if (subscribed) {
                        setAreaMapImage({ Image: response?.Image ?? "", naturalHeight: 0, naturalWidth: 0 })
                    }
                }).catch((error) => {
                    if (subscribed) {
                        console.error("GetAreaMap Error:", error)
                    }
                }).finally(() => {
                    if (subscribed) {
                        setIsMapLoading(false)
                    }
                })
            } else {
                if (subscribed) {
                    setIsMapLoading(false)
                }
            }
        }

        return () => {
            subscribed = false
        }
    }, [isAuthorized, inputArea])

    // Switch Tenant And Get Locations
    useEffect(() => {
        let subscribed = true

        if (isAuthorized) {
            setInputArea(0)
            setInputLocation(0)
            setAllLocations((prev) => prev?.length > 0 ? [] : prev)
            if (inputTenant) {
                setIsLoading(true)
                switchTenant(inputTenant).then(async (response) => {
                    if (subscribed) {
                        if (!response) {
                            await getLocations().then((response) => {
                                if (response && response?.length >= 0) {
                                    setAllLocations(response)
                                } else {
                                    setAllLocations((prev) => prev?.length > 0 ? [] : prev)
                                    console.error("GetAllLocations Error:", response)
                                }
                            }).catch((error) => {
                                setAllLocations((prev) => prev?.length > 0 ? [] : prev)
                                console.error("GetAllLocations Error:", error)
                            })
                        } else {
                            console.error("SwitchTenant Error:", response)
                        }
                    }
                }).catch((error) => {
                    console.error("SwitchTenant Error:", error)
                }).finally(() => {
                    setIsLoading(false)
                })
            }
        }

        return () => {
            subscribed = false
        }
    }, [isAuthorized, inputTenant])

    // Get All Tenants
    useEffect(() => {
        if (isAuthorized) {
            setIsAllTenantLoading(true)
            getTenants().then((response) => {
                if (response && response?.length >= 0) {
                    setAllTenants(response && response?.length > 0 ? response : [])
                    setInputTenant(response?.find((d) => d?.IsCurrent)?.TenantId ?? 0)
                } else {
                    setAllTenants((prev) => prev?.length > 0 ? [] : prev)
                    setInputTenant(0)
                    console.error("GetAllTenant Error:", response)
                }
            }).catch((error) => {
                setAllTenants((prev) => prev?.length > 0 ? [] : prev)
                setInputTenant(0)
                console.error("GetAllTenant Error:", error)
            }).finally(() => {
                setIsAllTenantLoading(false)
            })
        }
    }, [isAuthorized])

    // Getting XML files from assets folder
    useEffect(() => {
        if (isAuthorized) {
            axios.get("./assets/XMLTemplates/XMLTemplate.xml", {
                "Content-Type": "application/xml charset=utf-8"
            }).then((response) => {
                setXMLTemplate(response?.data ? response?.data : "")
            })
        }
    }, [isAuthorized])

    return (
        <main id="main" className="main">
            {(isLoading || isAllTenantLoading) && <Loader />}

            <div className="card main-card mb-0 p-4">
                {isAuthorized ? (
                    <>
                        {/* Input's */}
                        <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
                            {/* Tenant */}
                            <div className="flex-fill">
                                <div className="row">
                                    <div className="col-4 col-md-12 col-lg-12">
                                        <label className="mb-2" htmlFor="Tenant">Tenant</label>
                                    </div>
                                    <div className="col-8 col-md-12 col-lg-12">
                                        <select
                                            className="form-select"
                                            value={inputTenant}
                                            onChange={(e) => {
                                                setInputTenant(Number(e.target.value))
                                                setInputLocation(0)
                                                setInputArea(0)
                                                setAllSelectedSeats((prev) => prev?.length > 0 ? [] : prev)
                                            }}
                                        >
                                            <option value={0}>Select Tenant</option>
                                            {allTenants && allTenants?.length > 0 && (
                                                allTenants?.map((tenant, tenantIndex) =>
                                                    <option key={tenantIndex} value={tenant?.TenantId}>
                                                        {tenant?.TenantName ? tenant?.TenantName : "--"}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Location */}
                            <div className="flex-fill">
                                <div className="row">
                                    <div className="col-4 col-md-12 col-lg-12">
                                        <label className="mb-2" htmlFor="Location">Location</label>
                                    </div>
                                    <div className="col-8 col-md-12 col-lg-12">
                                        <select
                                            className="form-select"
                                            disabled={!inputTenant}
                                            value={inputLocation}
                                            onChange={(e) => {
                                                setInputLocation(Number(e.target.value))
                                                setInputArea(0)
                                                setAllSelectedSeats((prev) => prev?.length > 0 ? [] : prev)
                                            }}
                                        >
                                            <option value={0}>Select Location</option>
                                            {allLocations && allLocations?.length > 0 && (
                                                allLocations?.map((location, locationIndex) =>
                                                    <option key={locationIndex} value={location?.Id}>
                                                        {location?.Name ? location?.Name : "--"}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Area */}
                            {(activeTab === tabs?.Seats || activeTab === tabs?.Area) && (
                                <div className="flex-fill">
                                    <div className="row">
                                        <div className="col-4 col-md-12 col-lg-12">
                                            <label className="mb-2" htmlFor="Area">Area</label>
                                        </div>
                                        <div className="col-8 col-md-12 col-lg-12">
                                            <select
                                                className="form-select"
                                                disabled={!inputTenant || !inputLocation}
                                                value={inputArea}
                                                onChange={(e) => {
                                                    setInputArea(Number(e.target.value))
                                                    setAllSelectedSeats((prev) => prev?.length > 0 ? [] : prev)
                                                }}
                                            >
                                                <option value={0}>Select Area</option>
                                                {allAreas && allAreas?.length > 0 && (
                                                    allAreas?.map((area, areaIndex) =>
                                                        <option key={areaIndex} value={area?.Id}>
                                                            {area?.Name ? area?.Name : "--"}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="d-flex justify-content-between gap-3 mb-3">
                            {/* Toggle Button */}
                            <div className="toggle">
                                {tabList && tabList?.length > 0 && (
                                    tabList?.map((tab, tabIndex) =>
                                        <button
                                            key={tabIndex}
                                            type="button"
                                            title={tab?.Title}
                                            className={`btn toggle-btn${activeTab === tab?.Id ? " active" : ""}`}
                                            onClick={() => setActiveTab(tab?.Id)}
                                        >
                                            <tab.Icon />
                                        </button>
                                    )
                                )}
                            </div>

                            <div className="action-buttons flex-fill d-flex align-items-center flex-wrap justify-content-end gap-3">
                                {(activeTab === tabs?.Seats || activeTab === tabs?.Area) && (
                                    <button
                                        type="button"
                                        disabled={!editedNameList?.length}
                                        className="btn btn-primary px-3 py-2"
                                        onClick={() => UpdateSeatsByTenantIdLocationIdAndAreaId(inputTenant, inputLocation, inputArea)}
                                    >
                                        Save &#40;{editedNameList?.length}&#41;
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-primary px-3 py-2"
                                    disabled={(
                                        ((activeTab === tabs?.Seats || activeTab === tabs?.Area) && !allSeats?.length) ||
                                        (activeTab === tabs?.MeetingRooms && !meetingRoomsData?.MeetingRooms?.length)
                                    )}
                                    onClick={() => toggleAllItems()}
                                >
                                    {isAllSelected ? "Deselect All" : "Select All"}
                                </button>
                                <button
                                    type="button"
                                    disabled={(
                                        ((activeTab === tabs?.Seats || activeTab === tabs?.Area) && !allSelectedSeats?.length) ||
                                        (activeTab === tabs?.MeetingRooms && !selectedMeetingRooms?.length)
                                    )}
                                    data-bs-target="#printModal"
                                    className="btn btn-primary px-3 py-2"
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    Print &#40;{(activeTab === tabs?.Seats || activeTab === tabs?.Area) ? (
                                        allSelectedSeats?.length
                                    ) : activeTab === tabs?.MeetingRooms ? (
                                        selectedMeetingRooms?.length
                                    ) : (0)}&#41;
                                </button>
                            </div>
                        </div>

                        {activeTab === tabs?.MeetingRooms ? (
                            <div className="print-table">
                                <div className="table-row table-header">
                                    <div className="col-checkbox px-3 py-2 text-center">#</div>
                                    <div className="col-name px-3 py-2 text-center">Meeting Room Name</div>
                                    <div className="col-email px-3 py-2 text-center">Email</div>
                                    <div className="col-capacity px-3 py-2 text-center">Capacity</div>
                                </div>

                                {inputTenant && inputLocation && !meetingRoomsData?.IsLoading && meetingRoomsData?.MeetingRooms?.length > 0 ? (
                                    meetingRoomsData?.MeetingRooms?.map((meetingRoom, meetingRoomIndex) =>
                                        <div key={meetingRoomIndex} className="table-row">
                                            <div className="col-checkbox px-3 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox-input"
                                                    id={`checkbox-meetingroom-${meetingRoom?.Id}`}
                                                    checked={selectedMeetingRooms?.includes(meetingRoom)}
                                                    onChange={() => handleCheckboxClick(meetingRoom)}
                                                />
                                            </div>

                                            <div className="col-name px-3 py-2 text-center">
                                                <span>{meetingRoom?.Name ? meetingRoom?.Name : "--"}</span>
                                            </div>
                                            <div className="col-email px-3 py-2 text-center">
                                                <span>{meetingRoom?.Email ? meetingRoom?.Email : "--"}</span>
                                            </div>
                                            <div className="col-capacity px-3 py-2 text-center">
                                                <span>{meetingRoom?.Capacity ?? 0}</span>
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <div className="table-row">
                                        <div className="col-12 text-center">
                                            {!inputTenant ? (
                                                "Select Tenant!"
                                            ) : !inputLocation ? (
                                                "Select Location!"
                                            ) : meetingRoomsData?.IsLoading ? (
                                                "Loading ..."
                                            ) : !meetingRoomsData?.MeetingRooms?.length && (
                                                "No data in this table!"
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : activeTab === tabs?.Area ? (
                            <div className="print-table">
                                <div className="table-row table-header">
                                    <div className="table-col-9 px-3 py-2">Area Map</div>
                                </div>

                                {inputTenant && inputLocation && inputArea && !isMapLoading && areaMapImage?.Image ? (
                                    <div className="map-canvas">
                                        <div className="map-area">
                                            <img
                                                src={areaMapImage?.Image}
                                                className="map"
                                                onLoad={(e) =>
                                                    setAreaMapImage((prev) => ({
                                                        ...prev,
                                                        naturalHeight: e.target.naturalHeight,
                                                        naturalWidth: e.target.naturalWidth,
                                                    }))
                                                }
                                                alt="area-map"
                                            />
                                            {allSeats && allSeats.length > 0 && (
                                                allSeats?.map((seat, seatIndex) => (
                                                    <div
                                                        key={seatIndex}
                                                        className="card seat-card d-flex justify-content-center align-items-center px-2"
                                                        onClick={() => handleCheckboxClick(seat)}
                                                        style={{
                                                            ...getMapStyle(seat?.XCoord, seat?.YCoord, areaMapImage?.naturalWidth, areaMapImage?.naturalHeight, false, 0),
                                                        }}
                                                    >
                                                        {seat?.Name}
                                                        <div className={`${allSelectedSeats?.find((s) => s?.Id === seat?.Id) ? "map-chair-selected" : "map-chair"} d-flex justify-content-center align-items-center`}>
                                                            <IconChair height={18} width={18} color={allSelectedSeats?.find((s) => s?.Id === seat?.Id) ? "white" : "#1A327D"} />
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : isMapLoading ? (
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ height: "50vh", width: "100%" }}
                                    >
                                        <span>Loading...</span>
                                    </div>
                                ) : (!inputTenant || !inputLocation || !inputArea) ? (
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ height: "50vh", width: "100%" }}
                                    >
                                        {!inputTenant ? (
                                            <span>Select Tenant!</span>
                                        ) : !inputLocation ? (
                                            <span>Select Location!</span>
                                        ) : !inputArea && (
                                            <span>Select Area!</span>
                                        )}
                                    </div>
                                ) : !areaMapImage?.Image && (
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ height: "50vh", width: "100%" }}
                                    >
                                        <span>No map is defined for this area!</span>
                                    </div>
                                )}
                            </div>
                        ) : activeTab === tabs?.Seats && ( // Seat List Card
                            <div className="print-table">
                                <div className="table-row table-header">
                                    <div className="col-checkbox px-3 py-2 text-center">#</div>
                                    <div className="col-name-full px-3 py-2 text-center">Seat Name</div>
                                </div>

                                {inputTenant && inputLocation && inputArea && !isAllSeatsLoading && allSeats && allSeats?.length > 0 ? (
                                    allSeats?.map((seat, seatIndex) =>
                                        <div key={seatIndex} className="table-row">
                                            <div className="col-checkbox px-3 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox-input"
                                                    checked={allSelectedSeats?.includes(seat)}
                                                    onChange={() => handleCheckboxClick(seat)}
                                                />
                                            </div>

                                            <div className="col-name-full px-3 py-2">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter Seat"
                                                    value={seat?.Name ?? ""}
                                                    onChange={(e) => handleChangeName(seatIndex, e.target.value, "Name")}
                                                />
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <div className="table-row">
                                        <div className="col-12 text-center">
                                            {!inputTenant ? (
                                                "Select Tenant!"
                                            ) : !inputLocation ? (
                                                "Select Location!"
                                            ) : !inputArea ? (
                                                "Select Area!"
                                            ) : isAllSeatsLoading ? (
                                                "Loading ..."
                                            ) : !allSeats?.length && (
                                                "No data in this table!"
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <h3 className="text-center mt-4">
                        You Are Unauthorized!
                    </h3>
                )}
            </div>

            {/* Modal */}
            {isAuthorized && (
                <>
                    <div className={`modal fade${isModalOpen ? " show d-block" : ""}`} id="printModal">
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-body">
                                    <label className="mb-2">Printer Name</label>
                                    <input
                                        type="text" value={printerName}
                                        onChange={(e) => setPrinterName(e.target.value)}
                                        className="form-control" placeholder="Printer name"
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        data-bs-dismiss="modal"
                                        onClick={() => {
                                            setIsModalOpen(false)
                                            setPrinterName(window.localStorage.getItem(localStorageNames?.printerName) ?? defaultPrinterName)
                                        }}
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        data-bs-dismiss="modal"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            PrintAllLabels(
                                                printerName,
                                                (activeTab === tabs?.Seats || activeTab === tabs?.Area) ? (
                                                    allSelectedSeats
                                                ) : activeTab === tabs?.MeetingRooms ? (
                                                    selectedMeetingRooms
                                                ) : ([])
                                            )
                                        }}
                                        style={{ backgroundColor: "var(--app-theme-color)", border: "1px solid var(--app-theme-color)" }}
                                    >
                                        Print
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {isModalOpen && (<div className="modal-backdrop fade show" />)}
                </>
            )}
        </main>
    )
}
